import type { KeyConfig, KeyboardType } from "../../Keyboard/types";
import type {
  ChangeKeyEntry,
  FullBoardConfigData,
} from "@eweadn-qiankun/shared-types";
import { getKeyCodeMatrix } from "../../Keyboard/keyboardLayouts";
import {
  getKeyCodeInfo,
  KEYCODE_TO_WEB_KEY_MAP,
} from "../../../utils/keyCodeMapper";
import {
  KEYCODE_MAP_BEIYING,
  MEDIA_KEYS_BEIYING,
  MOUSE_KEYS_BEIYING,
  resolveBeiyingRemapUiKeycode,
} from "../../../utils/keyCodeMapperBeiying";
import {
  convertLayoutRows,
  getKeyWidthsByType,
  resolveLayoutKeyDisplayLabel,
  type SDKRowLayout,
} from "../../../utils/keyboardLayoutUtils";
import { usesSliceFirstRowLayout } from "../../../utils/sliceFirstRowKeyboardMatrix";
import {
  HFD_DK108_KNOB_ACTIONS,
  resolveDk108KnobDisplayKeycode,
  type HfdDk108KnobActionId,
  isHuafendaFnLayerSupportedDevice,
} from "@eweadn-qiankun/shared-utils";
/** 空键 / 穿透键：Fn 层未映射时显示 △（与子应用 convertLayoutRows 一致） */
export const INHERIT_BASE_LAYER_KEYCODES = new Set([0, 1]);

const FN_LAYER_PIERCE_KEYCODE = 1;

function isBeiyingProvider(solutionProvider?: string): boolean {
  const p = solutionProvider?.toLowerCase();
  return p === "beiying" || p === "beiyingmachine";
}

function isXingshanProvider(solutionProvider?: string): boolean {
  return solutionProvider?.toLowerCase() === "xingshan";
}

function findChangeKeyLayerData(
  configData: FullBoardConfigData | undefined,
  system: "WIN" | "MAC",
  layer: number
) {
  return configData?.changeKey?.find(
    (item) =>
      String(item.system).toUpperCase() === system &&
      Number(item.layer) === layer
  );
}

/** 快照条目是否为用户实际改键：仅以读盘/分享时写入的 sourceKeyName 为准 */
function isUserRemapEntry(entry: ChangeKeyEntry): boolean {
  return Boolean(entry.sourceKeyName?.trim());
}

/** 社区改键预览：与子应用 convertLayoutRows 一致，按 keyCode + 机型解析文案 */
export function resolveChangeKeyTargetLabel(
  keyCode: number | undefined,
  keyName: string | undefined,
  solutionProvider?: string,
  keyboardType?: string,
  firmwareRow?: number,
  firmwareCol?: number
): string | undefined {
  if (keyCode != null && keyCode !== 0) {
    const label = resolveLayoutKeyDisplayLabel(
      keyCode,
      (keyboardType ?? "87") as KeyboardType,
      {
        firmwareRow,
        firmwareCol,
        solutionProvider,
      }
    );
    if (label) return label;
  }
  const stored = keyName?.trim();
  return stored || undefined;
}

function isLayoutKey(key: KeyConfig): boolean {
  return (
    Boolean(key.uuid) &&
    key.code !== "__gap__" &&
    key.originalCol != null &&
    key.originalCol >= 0
  );
}

function findLayoutKeyAtFirmwareRow(
  layout: KeyConfig[][],
  firmwareRow: number,
  col: number
): KeyConfig | undefined {
  for (const row of layout) {
    for (const key of row) {
      if (!isLayoutKey(key)) continue;
      if (key.originalRow === firmwareRow && key.originalCol === col) {
        return key;
      }
    }
  }
  return undefined;
}

function findLayoutKeyAtUiRow(
  layout: KeyConfig[][],
  uiRow: number,
  col: number,
  keyboardType?: string
): KeyConfig | undefined {
  if (!usesSliceFirstRowLayout(keyboardType)) return undefined;

  for (const row of layout) {
    for (const key of row) {
      if (!isLayoutKey(key)) continue;
      if (key.originalCol !== col) continue;
      const storedRow = key.originalRow ?? 0;
      const resolvedUiRow = storedRow > 0 ? storedRow - 1 : storedRow;
      if (resolvedUiRow === uiRow) return key;
    }
  }
  return undefined;
}

/**
 * 旧版快照可能用 UI 行存 row；若该 (row,col) 已有固件键位则不再偏移，避免 F 行整行错位到 Q 行。
 */
export function normalizeChangeKeyEntriesForPreview(
  entries: ChangeKeyEntry[],
  layout: KeyConfig[][],
  keyboardType?: string
): ChangeKeyEntry[] {
  if (!usesSliceFirstRowLayout(keyboardType)) return entries;

  return entries.map((entry) => {
    if (findLayoutKeyAtFirmwareRow(layout, entry.row, entry.col)) {
      return entry;
    }

    const uiKey = findLayoutKeyAtUiRow(
      layout,
      entry.row,
      entry.col,
      keyboardType
    );
    if (uiKey?.originalRow != null) {
      return { ...entry, row: uiKey.originalRow };
    }

    return entry;
  });
}

function buildRemappedKey(
  key: KeyConfig,
  remapKeyCode: number | undefined,
  label: string,
  solutionProvider?: string,
  _keyboardType?: string
): KeyConfig {
  const provider = solutionProvider?.toLowerCase();
  const uiKeyCode =
    provider === "beiying" || provider === "beiyingmachine"
      ? resolveBeiyingRemapUiKeycode(remapKeyCode ?? 0)
      : remapKeyCode;

  const keyInfo = uiKeyCode != null ? getKeyCodeInfo(uiKeyCode) : undefined;

  let comm = keyInfo?.comm ?? key.comm ?? "";
  if (isBeiyingProvider(solutionProvider) && uiKeyCode != null) {
    const cap =
      KEYCODE_MAP_BEIYING.find(
        (item) => item.keycode === uiKeyCode || item.xsKeycode === uiKeyCode
      ) ??
      MEDIA_KEYS_BEIYING.find(
        (item) => item.keycode === uiKeyCode || item.xsKeycode === uiKeyCode
      ) ??
      MOUSE_KEYS_BEIYING.find(
        (item) => item.keycode === uiKeyCode || item.xsKeycode === uiKeyCode
      );
    if (cap && "comm" in cap && typeof cap.comm === "string") {
      comm = cap.comm;
    } else if (cap && "name" in cap && typeof cap.name === "string") {
      comm = cap.name;
    }
  }

  return {
    ...key,
    ...(uiKeyCode != null ? { keyCode: uiKeyCode } : {}),
    label,
    comm,
    code:
      uiKeyCode != null
        ? (KEYCODE_TO_WEB_KEY_MAP[uiKeyCode] ?? key.code)
        : key.code,
  };
}

/**
 * Fn 层通用：在 Base 布局坐标上按快照逐键覆盖（贝盈 ES98、星闪 87 等非 slice 型号）。
 */
function applyFnLayerOverlayRemaps(
  layout: KeyConfig[][],
  entries: ChangeKeyEntry[],
  solutionProvider?: string,
  keyboardType?: string
): KeyConfig[][] {
  const entryByPos = new Map<string, ChangeKeyEntry>();
  for (const entry of entries) {
    entryByPos.set(`${entry.row},${entry.col}`, entry);
  }

  return layout.map((row) =>
    row.map((key) => {
      if (!isLayoutKey(key)) return key;

      const firmwareRow = key.originalRow;
      const col = key.originalCol;
      if (firmwareRow == null || col == null) return key;

      const entry = entryByPos.get(`${firmwareRow},${col}`);
      if (!entry || entry.keyCode === 0) return key;

      const remapKeyCode = entry.keyCode;
      if (remapKeyCode === FN_LAYER_PIERCE_KEYCODE) {
        return buildRemappedKey(
          key,
          remapKeyCode,
          "△",
          solutionProvider,
          keyboardType
        );
      }

      const label =
        resolveChangeKeyTargetLabel(
          remapKeyCode,
          entry.keyName,
          solutionProvider,
          keyboardType,
          firmwareRow,
          col
        ) ?? entry.keyName;
      if (!label) return key;

      return buildRemappedKey(
        key,
        remapKeyCode,
        label,
        solutionProvider,
        keyboardType
      );
    })
  );
}

/**
 * 星闪等 slice 布局 Fn 层：快照拼 SDK 行矩阵后 convertLayoutRows。
 */
function buildXingshanFnLayerLayoutFromSnapshot(
  baseLayout: KeyConfig[][],
  entries: ChangeKeyEntry[],
  keyboardType: string
): KeyConfig[][] {
  const kt = keyboardType as KeyboardType;
  const matrix = getKeyCodeMatrix(kt);
  const widths = getKeyWidthsByType(kt);
  const entryByPos = new Map<string, ChangeKeyEntry>();
  for (const entry of entries) {
    entryByPos.set(`${entry.row},${entry.col}`, entry);
  }

  const physicalKeysByRow = new Map<number, KeyConfig[]>();
  for (const row of baseLayout) {
    for (const key of row) {
      if (!isLayoutKey(key)) continue;
      const firmwareRow = key.originalRow ?? 0;
      const list = physicalKeysByRow.get(firmwareRow) ?? [];
      list.push(key);
      physicalKeysByRow.set(firmwareRow, list);
    }
  }

  const sdkRows: SDKRowLayout[] = matrix.map((matrixRow, rowIndex) => {
    const keyboardLayout = [...matrixRow];
    const rowWidth = widths[rowIndex]?.length ?? keyboardLayout.length;
    while (keyboardLayout.length < rowWidth) {
      keyboardLayout.push(0);
    }

    for (const [pos, entry] of entryByPos) {
      const [rowStr, colStr] = pos.split(",");
      const row = Number(rowStr);
      const col = Number(colStr);
      if (row !== rowIndex || !Number.isFinite(col)) continue;
      if (entry.keyCode === 0) continue;
      keyboardLayout[col] = entry.keyCode;
    }

    for (const key of physicalKeysByRow.get(rowIndex) ?? []) {
      const col = key.originalCol!;
      if (!entryByPos.has(`${rowIndex},${col}`)) {
        keyboardLayout[col] = FN_LAYER_PIERCE_KEYCODE;
      }
    }

    return { layer: 1, row: rowIndex, keyboardLayout };
  });

  return convertLayoutRows(sdkRows, widths, kt, 1);
}

/**
 * 贝盈 Fn 层：快照拼 SDK 行矩阵后 convertLayoutRows（与子应用 getInitializeKeyboard + convertLayoutRows 一致）。
 */
function buildBeiyingFnLayerLayoutFromSnapshot(
  baseLayout: KeyConfig[][],
  entries: ChangeKeyEntry[],
  keyboardType: string
): KeyConfig[][] {
  const kt = keyboardType as KeyboardType;
  const matrix = getKeyCodeMatrix(kt);
  const widths = getKeyWidthsByType(kt);
  const entryByPos = new Map<string, ChangeKeyEntry>();
  for (const entry of entries) {
    entryByPos.set(`${entry.row},${entry.col}`, entry);
  }

  const physicalKeysByRow = new Map<number, KeyConfig[]>();
  for (const row of baseLayout) {
    for (const key of row) {
      if (!isLayoutKey(key)) continue;
      const firmwareRow = key.originalRow ?? 0;
      const list = physicalKeysByRow.get(firmwareRow) ?? [];
      list.push(key);
      physicalKeysByRow.set(firmwareRow, list);
    }
  }

  const sdkRows: SDKRowLayout[] = matrix.map((matrixRow, rowIndex) => {
    const keyboardLayout = [...matrixRow];
    const rowWidth = widths[rowIndex]?.length ?? keyboardLayout.length;
    while (keyboardLayout.length < rowWidth) {
      keyboardLayout.push(0);
    }

    for (const [pos, entry] of entryByPos) {
      const [rowStr, colStr] = pos.split(",");
      const row = Number(rowStr);
      const col = Number(colStr);
      if (row !== rowIndex || !Number.isFinite(col)) continue;
      if (entry.keyCode === 0) continue;
      keyboardLayout[col] = entry.keyCode;
    }

    for (const key of physicalKeysByRow.get(rowIndex) ?? []) {
      const col = key.originalCol!;
      if (!entryByPos.has(`${rowIndex},${col}`)) {
        keyboardLayout[col] = FN_LAYER_PIERCE_KEYCODE;
      }
    }

    return { layer: 1, row: rowIndex, keyboardLayout };
  });

  return convertLayoutRows(sdkRows, widths, kt, 1);
}

function resolveFnLayerPreviewLayout(
  layout: KeyConfig[][],
  entries: ChangeKeyEntry[],
  keyboardType: string,
  solutionProvider?: string
): KeyConfig[][] {
  if (isBeiyingProvider(solutionProvider)) {
    return buildBeiyingFnLayerLayoutFromSnapshot(layout, entries, keyboardType);
  }
  if (
    isXingshanProvider(solutionProvider) ||
    usesSliceFirstRowLayout(keyboardType)
  ) {
    return buildXingshanFnLayerLayoutFromSnapshot(
      layout,
      entries,
      keyboardType
    );
  }
  return applyFnLayerOverlayRemaps(
    layout,
    entries,
    solutionProvider,
    keyboardType
  );
}

/** Base 层：按 SDK 行矩阵叠加改键后 convertLayoutRows（对齐 useKeyboardLayout loadLayout） */
function buildBaseLayerLayoutViaSdkRows(
  referenceLayout: KeyConfig[][],
  entries: ChangeKeyEntry[],
  keyboardType: string,
  system: "WIN" | "MAC" = "WIN"
): KeyConfig[][] {
  const kt = keyboardType as KeyboardType;
  const matrix = getKeyCodeMatrix(kt);
  const widths = getKeyWidthsByType(kt);

  const entryByPos = new Map<string, number>();
  for (const entry of entries) {
    if (entry.keyCode === 0) continue;
    const kc = entry.keyCode;
    if (typeof kc === "number" && INHERIT_BASE_LAYER_KEYCODES.has(kc)) continue;
    if (!isUserRemapEntry(entry)) continue;
    entryByPos.set(`${entry.row},${entry.col}`, kc);
  }

  if (!entryByPos.size) {
    return referenceLayout;
  }

  const sdkRows: SDKRowLayout[] = matrix.map((matrixRow, rowIndex) => {
    const keyboardLayout = [...matrixRow];
    const rowWidth = widths[rowIndex]?.length ?? keyboardLayout.length;
    while (keyboardLayout.length < rowWidth) {
      keyboardLayout.push(0);
    }
    for (const [pos, kc] of entryByPos) {
      const [rowStr, colStr] = pos.split(",");
      const row = Number(rowStr);
      const col = Number(colStr);
      if (
        row === rowIndex &&
        Number.isFinite(col) &&
        col >= 0 &&
        col < keyboardLayout.length
      ) {
        keyboardLayout[col] = kc;
      }
    }
    return { layer: 0, row: rowIndex, keyboardLayout };
  });

  return convertLayoutRows(sdkRows, widths, kt, 0, system === "MAC" ? 1 : 0);
}

/** 改键层：合并 remap 到 layout 副本 */
export function applyChangeKeyRemaps(
  layout: KeyConfig[][],
  configData?: FullBoardConfigData,
  system: "WIN" | "MAC" = "WIN",
  layer = 0,
  solutionProvider?: string,
  keyboardType?: string
): KeyConfig[][] {
  const layerData = findChangeKeyLayerData(configData, system, layer);
  const normalizedEntries = normalizeChangeKeyEntriesForPreview(
    layerData?.keys ?? [],
    layout,
    keyboardType
  );

  if (layer !== 0) {
    return resolveFnLayerPreviewLayout(
      layout,
      normalizedEntries,
      keyboardType ?? "87",
      solutionProvider
    );
  }

  if (!normalizedEntries.length) {
    return layout;
  }

  return buildBaseLayerLayoutViaSdkRows(
    layout,
    normalizedEntries,
    keyboardType ?? "87",
    system
  );
}

/** 改键预览：已 remap 的键 uuid（须与当前展示 layout 一致，convertLayoutRows 会重建 uuid） */
export function getChangeKeySelectedUuids(
  displayLayout: KeyConfig[][],
  configData?: FullBoardConfigData,
  system: "WIN" | "MAC" = "WIN",
  layer = 0,
  _solutionProvider?: string,
  keyboardType?: string,
  /** 行号归一化用未 remap 矩阵；不传则用 displayLayout */
  normalizeLayout?: KeyConfig[][]
): string[] {
  const rowNormalizeLayout = normalizeLayout ?? displayLayout;
  const layerData = findChangeKeyLayerData(configData, system, layer);
  const normalizedEntries = normalizeChangeKeyEntriesForPreview(
    layerData?.keys ?? [],
    rowNormalizeLayout,
    keyboardType
  );

  const remappedPositions = new Set<string>();
  for (const entry of normalizedEntries) {
    if (entry.keyCode === 0) continue;
    if (
      layer === 0 &&
      typeof entry.keyCode === "number" &&
      INHERIT_BASE_LAYER_KEYCODES.has(entry.keyCode)
    ) {
      continue;
    }
    if (!isUserRemapEntry(entry)) {
      continue;
    }
    remappedPositions.add(`${entry.row},${entry.col}`);
  }
  if (!remappedPositions.size) return [];

  const uuids: string[] = [];
  for (const row of displayLayout) {
    for (const key of row) {
      if (!isLayoutKey(key)) continue;
      const firmwareRow = key.originalRow;
      const col = key.originalCol;
      if (firmwareRow == null || col == null) continue;
      if (remappedPositions.has(`${firmwareRow},${col}`)) {
        uuids.push(key.uuid!);
      }
    }
  }
  return uuids;
}

export interface ChangeKeyLayerOption {
  id: string;
  labelKey:
    | "keyboard.changeKey.layerWinBase"
    | "keyboard.changeKey.layerWinFn"
    | "keyboard.changeKey.layerWinFn2"
    | "keyboard.changeKey.layerNormalBase"
    | "keyboard.changeKey.layerFnShort"
    | "keyboard.changeKey.layerMacBase"
    | "keyboard.changeKey.layerMacFn"
    | "keyboard.changeKey.layerMacFn2";
  system: "WIN" | "MAC";
  layer: number;
}

const HFD_FN_LAYER_CATALOG: ChangeKeyLayerOption[] = [
  {
    id: "WIN-0",
    labelKey: "keyboard.changeKey.layerNormalBase",
    system: "WIN",
    layer: 0,
  },
  {
    id: "WIN-1",
    labelKey: "keyboard.changeKey.layerFnShort",
    system: "WIN",
    layer: 1,
  },
];

const CHANGE_KEY_LAYER_CATALOG: ChangeKeyLayerOption[] = [
  {
    id: "WIN-0",
    labelKey: "keyboard.changeKey.layerWinBase",
    system: "WIN",
    layer: 0,
  },
  {
    id: "WIN-1",
    labelKey: "keyboard.changeKey.layerWinFn",
    system: "WIN",
    layer: 1,
  },
  {
    id: "MAC-0",
    labelKey: "keyboard.changeKey.layerMacBase",
    system: "MAC",
    layer: 0,
  },
  {
    id: "MAC-1",
    labelKey: "keyboard.changeKey.layerMacFn",
    system: "MAC",
    layer: 1,
  },
];

/** Gamma75：Base + Fn1 + Fn2 */
const GAMMA75_CHANGE_KEY_LAYER_CATALOG: ChangeKeyLayerOption[] = [
  {
    id: "WIN-0",
    labelKey: "keyboard.changeKey.layerWinBase",
    system: "WIN",
    layer: 0,
  },
  {
    id: "WIN-1",
    labelKey: "keyboard.changeKey.layerWinFn",
    system: "WIN",
    layer: 1,
  },
  {
    id: "WIN-2",
    labelKey: "keyboard.changeKey.layerWinFn2",
    system: "WIN",
    layer: 2,
  },
  {
    id: "MAC-0",
    labelKey: "keyboard.changeKey.layerMacBase",
    system: "MAC",
    layer: 0,
  },
  {
    id: "MAC-1",
    labelKey: "keyboard.changeKey.layerMacFn",
    system: "MAC",
    layer: 1,
  },
  {
    id: "MAC-2",
    labelKey: "keyboard.changeKey.layerMacFn2",
    system: "MAC",
    layer: 2,
  },
];

const RONGYUAN_CHANGE_KEY_LAYER_CATALOG: ChangeKeyLayerOption[] = [
  {
    id: "WIN-0",
    labelKey: "keyboard.changeKey.layerNormalBase",
    system: "WIN",
    layer: 0,
  },
  {
    id: "WIN-1",
    labelKey: "keyboard.changeKey.layerFnShort",
    system: "WIN",
    layer: 1,
  },
];

function getProviderLayerCatalog(
  solutionProvider?: string,
  currentDeviceSystem = 0,
  equipment?: {
    displayname?: string;
    productName?: string;
    keyboardType?: string;
    solutionRovider?: string;
  }
): ChangeKeyLayerOption[] {
  if (solutionProvider?.toLowerCase() === "rongyuan") {
    return RONGYUAN_CHANGE_KEY_LAYER_CATALOG;
  }
  if (solutionProvider?.toLowerCase() === "huafenda") {
    if (isHuafendaFnLayerSupportedDevice(equipment)) {
      return HFD_FN_LAYER_CATALOG;
    }
    return CHANGE_KEY_LAYER_CATALOG.filter(
      (item) => item.system === "WIN" && item.layer === 0
    );
  }
  const activeSystem = currentDeviceSystem === 1 ? "MAC" : "WIN";
  const catalog =
    equipment?.keyboardType === "gamma75"
      ? GAMMA75_CHANGE_KEY_LAYER_CATALOG
      : CHANGE_KEY_LAYER_CATALOG;
  return catalog.filter((item) => item.system === activeSystem);
}

/** 从板载快照解析 DK108 旋钮三向绑定（performance.hfdWheelKeys；无则出厂默认） */
export function resolveDk108KnobBindingsFromConfig(
  configData?: FullBoardConfigData
): Record<HfdDk108KnobActionId, number> {
  const perf = configData?.performance as Record<string, unknown> | undefined;
  const stored = perf?.hfdWheelKeys as Record<string, number> | undefined;
  const result: Record<HfdDk108KnobActionId, number> = {
    press: 0,
    clockwise: 0,
    counterClockwise: 0,
  };

  for (const action of HFD_DK108_KNOB_ACTIONS) {
    const fromStored = Number(stored?.[String(action.keyIndex)] ?? 0);
    result[action.id] = resolveDk108KnobDisplayKeycode(action.id, fromStored);
  }
  return result;
}

export function getChangeKeyLayerOptions(
  _configData?: FullBoardConfigData,
  meta?: {
    solutionProvider?: string;
    currentDeviceSystem?: number;
    displayname?: string;
    productName?: string;
    keyboardType?: string;
  }
): ChangeKeyLayerOption[] {
  const currentDeviceSystem = meta?.currentDeviceSystem ?? 0;
  const providerCatalog = getProviderLayerCatalog(
    meta?.solutionProvider,
    currentDeviceSystem,
    meta
  );

  return providerCatalog.length
    ? providerCatalog
    : [CHANGE_KEY_LAYER_CATALOG[0]];
}
